/**
 * ClientSentinelCard — "SAM.gov Sentinel" config block on the Client Profile.
 *
 * Lets a consultant manage the SAM.gov API key + NAICS/PSC/agency-prefs for a
 * client organisation that hasn't necessarily opened a FedBD account yet
 * (Pattern A in the v3.18.22 collaboration spec). When the client DOES have a
 * FedBD account, this same card mirrors the values entered by the contractor
 * in Workspace Settings → Integrations (data lives on the same
 * `organizations` row).
 *
 * Also exposes "Run Scan Now" so the consultant can trigger a scan on behalf
 * of the client without leaving the Client Profile. Results land in the
 * consultant's Marketplace Sentinel filtered to this client.
 *
 * v3.18.22 — Consultant ↔ Contractor SAM.gov Sentinel collaboration.
 * v3.18.33 — Set-aside qualifications chips + GSA eLibrary URL import.
 *            Parsing a gsaelibrary.gsa.gov contractorInfo URL auto-populates:
 *              • set_aside_qualifications (SAM Sentinel filter)
 *              • gsa_sin_codes (eBuy SIN profile — Gold match)
 *              • gsa_schedule_contracts (eBuy contract numbers)
 *            After "Apply to Profile" the EbuyProfilePanel is reloaded via the
 *            onElibraryApplied() callback passed from ClientProfile.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import {
  Radar,
  KeyRound,
  RefreshCw,
  Save,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Trash2,
  Sparkles,
  Award,
  Link2,
  ExternalLink,
  X,
  Building2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const splitCsv = (value) =>
  (value || '')
    .split(/[,\n]/g)
    .map((s) => s.trim())
    .filter(Boolean);

// SAM.gov set-aside codes — must stay in sync with WorkspaceSettings.jsx
const SET_ASIDE_OPTIONS = [
  { code: 'SBA',      label: 'Total Small Business' },
  { code: 'SBP',      label: 'Partial Small Business' },
  { code: '8A',       label: '8(a) Set-Aside' },
  { code: '8AN',      label: '8(a) Sole Source' },
  { code: 'HZC',      label: 'HUBZone' },
  { code: 'HZS',      label: 'HUBZone Sole Source' },
  { code: 'WOSB',     label: 'WOSB' },
  { code: 'WOSBSS',   label: 'WOSB Sole Source' },
  { code: 'EDWOSB',   label: 'EDWOSB' },
  { code: 'EDWOSBSS', label: 'EDWOSB Sole Source' },
  { code: 'SDVOSBC',  label: 'SDVOSB' },
  { code: 'SDVOSBS',  label: 'SDVOSB Sole Source' },
  { code: 'VOSBC',    label: 'VOSB' },
  { code: 'VOSBS',    label: 'VOSB Sole Source' },
];

export default function ClientSentinelCard({ clientId, clientName, onElibraryApplied }) {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  // Separate in-flight flags so each button only disables/spins its own action.
  const [savingKey, setSavingKey] = useState(false);
  const [savingCodes, setSavingCodes] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [config, setConfig] = useState(null);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [naicsInput, setNaicsInput] = useState('');
  const [pscInput, setPscInput] = useState('');
  const [agencyInput, setAgencyInput] = useState('');
  const [setAsideQuals, setSetAsideQuals] = useState([]);

  // eLibrary import state
  const [elibraryUrl, setElibraryUrl] = useState('');
  const [elibraryData, setElibraryData] = useState(null);
  const [elibraryParsing, setElibraryParsing] = useState(false);
  const [elibraryApplying, setElibraryApplying] = useState(false);

  // `silent: true` refreshes config without swapping the card for a skeleton —
  // used after a save so the form stays mounted and the user keeps their place.
  const reload = useCallback(async ({ silent = false } = {}) => {
    if (!clientId) return;
    if (!silent) setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/clients/${clientId}/sentinel-config`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setConfig(body);
      setNaicsInput((body.target_naics_codes || []).join(', '));
      setPscInput((body.target_psc_codes || []).join(', '));
      setAgencyInput((body.agency_preferences || []).join(', '));
      setSetAsideQuals(body.set_aside_qualifications || []);
    } catch (e) {
      console.error(e);
      toast.error('Could not load Sentinel config');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [clientId, getAuthHeaders]);

  useEffect(() => { reload(); }, [reload]);

  /** PUT a partial sentinel-config patch. Every field is optional server-side,
   *  so each caller sends only the fields its button is labelled for. */
  const putConfig = async (payload) => {
    const res = await fetch(`${API_URL}/api/clients/${clientId}/sentinel-config`, {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.detail || `HTTP ${res.status}`);
    }
    return res.json().catch(() => ({}));
  };

  // Saves ONLY the API key. The key is validated against SAM.gov server-side
  // before it is stored, so this can fail for reasons unrelated to the key
  // itself (e.g. a 429 rate-limit) — in that case we keep what the user typed
  // so a retry doesn't mean re-pasting it.
  const handleSaveKey = async () => {
    if (savingKey || !apiKeyInput) return;
    setSavingKey(true);
    try {
      await putConfig({ sam_gov_api_key: apiKeyInput });
      toast.success('SAM.gov API key saved.');
      setApiKeyInput('');
      await reload({ silent: true });
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Could not save API key', { duration: 8000 });
    } finally {
      setSavingKey(false);
    }
  };

  // Saves ONLY the targeting profile — never touches the stored API key.
  const handleSaveCodes = async () => {
    if (savingCodes) return;
    setSavingCodes(true);
    try {
      await putConfig({
        target_naics_codes: splitCsv(naicsInput),
        target_psc_codes: splitCsv(pscInput),
        agency_preferences: splitCsv(agencyInput),
        set_aside_qualifications: setAsideQuals,
      });
      toast.success('Target codes saved.');
      await reload({ silent: true });
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Could not save target codes');
    } finally {
      setSavingCodes(false);
    }
  };

  const handleClearKey = async () => {
    if (!window.confirm('Remove the SAM.gov API key for this client?')) return;
    setSavingKey(true);
    try {
      await putConfig({ sam_gov_api_key: '' });
      toast.success('API key cleared.');
      await reload({ silent: true });
    } catch (e) {
      toast.error(e.message || 'Could not clear key');
    } finally {
      setSavingKey(false);
    }
  };

  const handleRunScan = async () => {
    if (scanning) return;
    setScanning(true);
    try {
      const res = await fetch(`${API_URL}/api/clients/${clientId}/sentinel-scan`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `HTTP ${res.status}`);
      }
      const body = await res.json();
      toast.success(body.message || 'Scan initiated.');
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  // ── GSA eLibrary import ──────────────────────────────────────────────────

  const handleParseElibrary = async () => {
    const url = elibraryUrl.trim();
    if (!url) return;
    setElibraryParsing(true);
    setElibraryData(null);
    try {
      const res = await fetch(`${API_URL}/api/ebuy/elibrary/parse`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setElibraryData(data);
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to parse eLibrary page');
    } finally {
      setElibraryParsing(false);
    }
  };

  const handleApplyElibrary = async () => {
    if (!elibraryData) return;
    setElibraryApplying(true);
    try {
      // 1. Update sentinel config — set-aside qualifications
      const sentRes = await fetch(`${API_URL}/api/clients/${clientId}/sentinel-config`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          set_aside_qualifications: elibraryData.set_aside_qualifications || [],
        }),
      });
      if (!sentRes.ok) {
        const err = await sentRes.json().catch(() => ({}));
        throw new Error(err?.detail || `Sentinel save failed: HTTP ${sentRes.status}`);
      }

      // 2. Update eBuy profile — SIN codes + contract numbers
      const ebuyRes = await fetch(
        `${API_URL}/api/ebuy/profile?organization_id=${encodeURIComponent(clientId)}`,
        {
          method: 'PUT',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            gsa_sin_codes: elibraryData.gsa_sin_codes || [],
            gsa_schedule_contracts: elibraryData.gsa_schedule_contracts || [],
          }),
        },
      );
      if (!ebuyRes.ok) {
        const err = await ebuyRes.json().catch(() => ({}));
        throw new Error(err?.detail || `eBuy profile save failed: HTTP ${ebuyRes.status}`);
      }

      const saCount = elibraryData.set_aside_qualifications?.length || 0;
      const sinCount = elibraryData.gsa_sin_codes?.length || 0;
      const ctrCount = elibraryData.gsa_schedule_contracts?.length || 0;
      toast.success(
        `eLibrary imported: ${saCount} set-aside${saCount !== 1 ? 's' : ''}, ` +
        `${sinCount} SIN${sinCount !== 1 ? 's' : ''}, ` +
        `${ctrCount} contract${ctrCount !== 1 ? 's' : ''}.`,
      );

      // 3. Refresh this card and signal parent to reload EbuyProfilePanel
      setElibraryUrl('');
      setElibraryData(null);
      await reload({ silent: true });
      onElibraryApplied?.();
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Apply failed');
    } finally {
      setElibraryApplying(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Card className="border-slate-200 bg-white shadow-sm" data-testid="client-sentinel-card">
        <CardContent className="p-6 space-y-3">
          <Skeleton className="h-6 w-1/3" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  const naicsCount = splitCsv(naicsInput).length;
  const canScan = naicsCount > 0;

  return (
    <Card className="border-slate-200 bg-white shadow-sm" data-testid="client-sentinel-card">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <div className="w-8 h-8 bg-cyan-100 rounded-lg flex items-center justify-center">
              <Radar className="w-4 h-4 text-cyan-700" />
            </div>
            SAM.gov Sentinel
          </CardTitle>
          <div className="flex items-center gap-2">
            {config?.has_sam_gov_api_key ? (
              <Badge className="bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> API key on file
              </Badge>
            ) : (
              <Badge variant="outline" className="text-amber-700 border-amber-300 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> No API key
              </Badge>
            )}
          </div>
        </div>
        <p className="text-xs text-slate-500">
          Configure the SAM.gov API key and target codes for <strong>{clientName}</strong>. Scans run against this profile and surface in the consultant&apos;s Marketplace Sentinel.
        </p>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* API key row */}
        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-600 flex items-center gap-1">
            <KeyRound className="w-3 h-3" />
            SAM.gov API key
          </label>
          {config?.has_sam_gov_api_key && (
            <p className="text-xs text-slate-500 mb-1">
              Current: <code className="px-1 py-0.5 bg-slate-100 rounded">{config.sam_gov_api_key_masked}</code>
              {config.sam_gov_last_sync && (
                <span className="ml-2">· last sync {new Date(config.sam_gov_last_sync).toLocaleString()}</span>
              )}
            </p>
          )}
          <div className="flex gap-2">
            <Input
              type="password"
              placeholder={config?.has_sam_gov_api_key ? 'Enter new key to replace' : 'Paste SAM.gov API key'}
              value={apiKeyInput}
              onChange={(e) => setApiKeyInput(e.target.value)}
              disabled={savingKey}
              className="font-mono text-sm"
              data-testid="client-sentinel-api-key-input"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleSaveKey}
              disabled={!apiKeyInput || savingKey}
              data-testid="client-sentinel-save-key-btn"
            >
              {savingKey ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              <span className="ml-1">{savingKey ? 'Checking with SAM.gov…' : 'Save key'}</span>
            </Button>
            {config?.has_sam_gov_api_key && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearKey}
                disabled={savingKey}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="client-sentinel-clear-key-btn"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
          </div>
          <p className="text-[11px] text-slate-400">
            Key is validated against SAM.gov before saving. Reads return only the last 4 characters.
          </p>
        </div>

        {/* Target codes row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-600">
              Target NAICS codes <span className="text-slate-400">(comma-sep)</span>
            </label>
            <Input
              placeholder="541512, 541513"
              value={naicsInput}
              onChange={(e) => setNaicsInput(e.target.value)}
              disabled={savingCodes}
              data-testid="client-sentinel-naics-input"
              className="font-mono text-sm"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-600">
              Target PSC codes <span className="text-slate-400">(comma-sep)</span>
            </label>
            <Input
              placeholder="D310, R425"
              value={pscInput}
              onChange={(e) => setPscInput(e.target.value)}
              disabled={savingCodes}
              data-testid="client-sentinel-psc-input"
              className="font-mono text-sm"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-600">
              Preferred agencies <span className="text-slate-400">(comma-sep)</span>
            </label>
            <Input
              placeholder="DOD, NASA, GSA"
              value={agencyInput}
              onChange={(e) => setAgencyInput(e.target.value)}
              disabled={savingCodes}
              data-testid="client-sentinel-agency-input"
              className="font-mono text-sm"
            />
          </div>
        </div>

        {/* Set-aside qualifications */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-slate-600 flex items-center gap-1">
            <Award className="w-3 h-3" />
            Set-aside qualifications
            <span className="text-slate-400 font-normal ml-1">(SAM.gov scan filter)</span>
          </label>
          <div className="flex flex-wrap gap-1.5">
            {SET_ASIDE_OPTIONS.map(({ code, label }) => {
              const active = setAsideQuals.includes(code);
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() =>
                    setSetAsideQuals((prev) =>
                      active ? prev.filter((c) => c !== code) : [...prev, code],
                    )
                  }
                  disabled={savingCodes}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    active
                      ? 'bg-amber-100 border-amber-400 text-amber-800 font-medium'
                      : 'bg-slate-50 border-slate-200 text-slate-500 hover:border-slate-300 hover:bg-slate-100'
                  }`}
                  data-testid={`client-sentinel-setaside-${code}`}
                >
                  {code} <span className="text-[10px] opacity-70">{label}</span>
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-400">
            Auto-populated from GSA eLibrary below. Active codes restrict SAM.gov scans to matching set-aside opportunities only.
          </p>
        </div>

        {/* GSA eLibrary URL import */}
        <div className="space-y-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-cyan-100 rounded flex items-center justify-center flex-shrink-0">
              <Link2 className="w-3.5 h-3.5 text-cyan-700" />
            </div>
            <span className="text-xs font-semibold text-slate-700">Import from GSA eLibrary</span>
            <a
              href="https://www.gsaelibrary.gsa.gov"
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto text-[10px] text-blue-500 hover:underline flex items-center gap-0.5 flex-shrink-0"
            >
              Open eLibrary <ExternalLink className="w-2.5 h-2.5 ml-0.5" />
            </a>
          </div>
          <p className="text-[11px] text-slate-500">
            Paste the client&apos;s GSA eLibrary contractor page URL to auto-populate set-aside qualifications, awarded SIN codes, and contract numbers.
          </p>
          <div className="flex gap-2">
            <Input
              placeholder="https://www.gsaelibrary.gsa.gov/ElibMain/contractorInfo.do?contractNumber=…"
              value={elibraryUrl}
              onChange={(e) => setElibraryUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleParseElibrary();
                }
              }}
              disabled={elibraryParsing || elibraryApplying}
              className="text-xs font-mono flex-1"
              data-testid="client-elibrary-url-input"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleParseElibrary}
              disabled={!elibraryUrl.trim() || elibraryParsing || elibraryApplying}
              className="border-cyan-200 text-cyan-700 hover:bg-cyan-50 whitespace-nowrap"
              data-testid="client-elibrary-parse-btn"
            >
              {elibraryParsing
                ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                : <Link2 className="w-3.5 h-3.5 mr-1" />}
              Import
            </Button>
          </div>

          {/* Preview panel — shown after successful parse */}
          {elibraryData && (
            <div className="space-y-3 pt-2 border-t border-slate-200">
              {/* Contractor name / UEI / NAICS */}
              {(elibraryData.contractor_name || elibraryData.sam_uei) && (
                <div className="flex items-center gap-2 flex-wrap text-xs text-slate-700">
                  <Building2 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                  {elibraryData.contractor_name && (
                    <span className="font-medium">{elibraryData.contractor_name}</span>
                  )}
                  {elibraryData.naics_code && (
                    <Badge variant="outline" className="text-xs border-slate-200 text-slate-500 px-1.5 py-0">
                      NAICS {elibraryData.naics_code}
                    </Badge>
                  )}
                  {elibraryData.sam_uei && (
                    <span className="text-slate-400 font-mono text-[10px]">UEI: {elibraryData.sam_uei}</span>
                  )}
                </div>
              )}

              {/* Set-aside qualifications preview */}
              {elibraryData.set_aside_qualifications?.length > 0 ? (
                <div>
                  <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                    Set-aside qualifications ({elibraryData.set_aside_qualifications.length})
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {elibraryData.set_aside_qualifications.map((code) => (
                      <Badge
                        key={code}
                        className="bg-amber-50 border border-amber-300 text-amber-800 text-xs"
                      >
                        {code}
                      </Badge>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-[11px] text-slate-400 italic">
                  No socio-economic indicators found on this page.
                </p>
              )}

              {/* Contracts + SINs preview */}
              {elibraryData.contracts?.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                    Schedule contracts &amp; SINs ({elibraryData.contracts.length})
                  </p>
                  <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                    {elibraryData.contracts.map((c) => (
                      <div
                        key={c.contract_number}
                        className="text-xs bg-white rounded border border-slate-200 p-2"
                      >
                        {/* Contract header */}
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-mono font-semibold text-slate-800">
                            {c.contract_number}
                          </span>
                          {c.title && (
                            <span className="text-slate-500 text-[10px]">{c.title}</span>
                          )}
                          {c.period_end && (
                            <span className="text-slate-400 text-[10px] ml-auto">exp. {c.period_end}</span>
                          )}
                        </div>

                        {/* SINs — use sin_details (with descriptions) when available,
                            fall back to flat sin_codes array */}
                        {(c.sin_details?.length > 0 || c.sin_codes?.length > 0) && (
                          <div className="space-y-0.5 max-h-32 overflow-y-auto">
                            {(c.sin_details || c.sin_codes.map((s) => ({ sin: s, description: '' }))).map((entry) => {
                              const sin = typeof entry === 'string' ? entry : entry.sin;
                              const desc = typeof entry === 'string' ? '' : entry.description;
                              return (
                                <div key={sin} className="flex items-baseline gap-1.5" title={desc}>
                                  <Badge
                                    variant="outline"
                                    className="text-[10px] border-cyan-200 text-cyan-700 px-1.5 py-0 flex-shrink-0"
                                  >
                                    {sin}
                                  </Badge>
                                  {desc && (
                                    <span className="text-[10px] text-slate-500 truncate">{desc}</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Apply / Discard */}
              <div className="flex items-center gap-2 pt-1 flex-wrap">
                <Button
                  size="sm"
                  onClick={handleApplyElibrary}
                  disabled={elibraryApplying}
                  className="bg-cyan-700 hover:bg-cyan-800 text-white"
                  data-testid="client-elibrary-apply-btn"
                >
                  {elibraryApplying
                    ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                    : <CheckCircle2 className="w-3 h-3 mr-1" />}
                  Apply to Profile
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setElibraryData(null); setElibraryUrl(''); }}
                  disabled={elibraryApplying}
                  className="text-slate-500 hover:text-slate-700"
                  data-testid="client-elibrary-discard-btn"
                >
                  <X className="w-3 h-3 mr-1" />
                  Discard
                </Button>
                <span className="ml-auto text-[10px] text-slate-400">
                  Saves set-asides · SIN codes · contracts
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Save profile + Run Scan actions */}
        <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSaveCodes}
            disabled={savingCodes}
            data-testid="client-sentinel-save-profile-btn"
          >
            {savingCodes ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
            Save target codes
          </Button>
          <Button
            size="sm"
            onClick={handleRunScan}
            disabled={!canScan || scanning}
            className="bg-cyan-700 hover:bg-cyan-800 text-white"
            data-testid="client-sentinel-run-scan-btn"
          >
            {scanning ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
            Run Scan Now
          </Button>
        </div>
        {!canScan && (
          <p className="text-[11px] text-amber-600 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Add at least one target NAICS code before running a scan.
          </p>
        )}

        {/* Daily usage indicator */}
        {config && (config.sam_gov_daily_usage > 0) && (
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <RefreshCw className="w-3 h-3" />
            {config.sam_gov_daily_usage} SAM.gov API call(s) used today.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
